import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import {
  Panel,
  Footer,
  Button,
  DataLossPrompt,
  TextField,
  PhoneInput,
} from 'melp-design/components';
import { useMe } from 'state/Administrators';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { useUpdateParentCompanySettings } from 'store/companies';
import { usePredefinedToasts } from 'utils/Toast';

interface Values {
  merchandiseContactPersonName: string;
  merchandiseContactPersonPhone: string;
  merchandiseContactPersonEmail: string;
}

const INITIAL_VALUES: Values = {
  merchandiseContactPersonName: '',
  merchandiseContactPersonPhone: '',
  merchandiseContactPersonEmail: '',
};

export const ShopInternalSettings: FC = () => {
  const { t } = useTranslation();
  const { me, hasEditPermission } = useMe();
  const canEdit = hasEditPermission(['internalMerch']);
  const predefinedToasts = usePredefinedToasts();

  const { mutate: updateCompanySettings, isLoading: isUpdating } =
    useUpdateParentCompanySettings();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  useEffect(() => {
    if (me?.parentCompany) {
      reset({
        merchandiseContactPersonName:
          me.parentCompany.settings.merchandiseContactPersonName,
        merchandiseContactPersonPhone:
          me.parentCompany.settings.merchandiseContactPersonPhone,
        merchandiseContactPersonEmail:
          me.parentCompany.settings.merchandiseContactPersonEmail,
      });
    }
  }, [me, reset]);

  const onSubmit: SubmitHandler<Values> = (values) => {
    updateCompanySettings(values, {
      onSuccess: () => {
        predefinedToasts.success.updated();
      },
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={2}>
          <Panel title={t('sidebar.companySettingsGeneralSettings')}>
            <Stack gap={2}>
              <Controller
                name="merchandiseContactPersonName"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <TextField
                      label={t('marketplaceOrders.contactPerson')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />

              <Controller
                name="merchandiseContactPersonEmail"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <TextField
                      label={t('marketplaceOrders.contactEmail')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      type="email"
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />

              <Controller
                name="merchandiseContactPersonPhone"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <PhoneInput
                    label={t('marketplaceOrders.contactPhone')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />
            </Stack>
          </Panel>
        </Stack>

        <Footer visible={isDirty}>
          <Stack direction="row" justifyContent="end" gap={2}>
            <Button
              label={t('common.cancel')}
              variant="neutral-outline"
              onClick={() => {
                reset(undefined, { keepDirty: false });
              }}
            />
            <Button
              label={t('common.save')}
              type="submit"
              disabled={isUpdating || !canEdit}
            />
          </Stack>
        </Footer>
      </form>

      <DataLossPrompt when={canEdit && isDirty} />
    </>
  );
};
